import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick, sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';
import SignInFieldsWrapper from 'garaje/components/sign-in-fields/wrapper';
import SlideToggle from 'garaje/components/slide-toggle';
import QuestionOption from 'garaje/components/visitors/questionnaires/form/section/question-option';
import TranslatableContent from 'garaje/components/visitors/questionnaires/form/translatable-content';
import type QuestionnaireField from 'garaje/models/questionnaire-field';
import type QuestionnaireFieldOption from 'garaje/models/questionnaire-field-option';
import autoresize from 'garaje/modifiers/autoresize';
import { SignInFieldKind } from 'garaje/utils/enums';
import type { LocaleKey } from 'garaje/utils/locale-options';
import type QuestionnaireValidator from 'garaje/utils/questionnaire-validator';
import { cached } from 'tracked-toolbox';

// The most number of options that can be added to a question
const MAX_OPTIONS = 20;

interface VisitorsQuestionnairesFormSectionQuestionSignature {
  Args: {
    defaultLocale: LocaleKey;
    deleteQuestion: (question: QuestionnaireField) => void;
    dragGroupName: string;
    inputId: string;
    isDeletable: boolean;
    isDraggable: boolean;
    isRTL?: boolean;
    onChange: (models: Model[]) => void;
    question: QuestionnaireField;
    readOnly?: boolean;
    selectedLocale: LocaleKey;
    validator: QuestionnaireValidator;
  };
  Element: HTMLDivElement;
}

export default class VisitorsQuestionnairesFormSectionQuestion extends Component<VisitorsQuestionnairesFormSectionQuestionSignature> {
  @service declare store: Store;

  @tracked newOption: string | null = '';

  get canDragOptions(): boolean {
    // Don't allow options to be reordered if:
    // 1) we're read-only, or
    // 2) there is only one option, or
    // 3) the user is viewing a non-default language
    if (this.args.readOnly) return false;
    if (!this.hasMultipleOptions) return false;
    if (this.isTranslating) return false;
    return true;
  }

  get canToggleSingleOrMultipleSelection(): boolean {
    // Don't allow the single/multiple toggle to be changed if:
    // 1) we're read-only, or
    // 2) the user is viewing a non-default language
    if (this.args.readOnly || this.isTranslating) return false;
    return true;
  }

  get dragGroupNameOptions(): string {
    return `section-${guidFor(this.args.question)}-question-options`;
  }

  get hasMultipleOptions(): boolean {
    return this.options.length > 1;
  }

  get hasErrors(): boolean {
    return this.args.validator.questionErrors(this.args.question).length > 0;
  }

  get hasMaxOptions(): boolean {
    return this.options.length >= MAX_OPTIONS;
  }

  get isMultipleSelection(): boolean {
    return this.args.question.kind == SignInFieldKind.MULTIPLE_SELECTION;
  }

  get isTranslating(): boolean {
    return this.args.selectedLocale !== this.args.defaultLocale;
  }

  @cached
  get localizedQuestion(): QuestionnaireField {
    return this.args.question.forLocale(this.args.selectedLocale, this.args.defaultLocale);
  }

  @cached
  get options(): QuestionnaireFieldOption[] {
    return this.args.question.questionnaireFieldOptions.filter((option) => !option.isDeleted);
  }

  get showAddOptionButton(): boolean {
    // Don't show the "Add option" button if:
    // 1) we're read-only, or
    // 2) the user is viewing a non-default language.
    // If the question already has the maximum number of allowed options, show the button, but in a "disabled" state.
    if (this.args.readOnly || this.isTranslating) return false;
    return true;
  }

  get showDeleteOptionButtons(): boolean {
    // Don't show delete buttons for options if:
    // 1) we're read-only, or
    // 2) there are only two options. (We don't want to have a question that has only one choice), or
    // 3) the user is viewing a non-default language
    if (this.args.readOnly) return false;
    if (this.options.length <= 2) return false;
    if (this.isTranslating) return false;
    return true;
  }

  @action
  addNewOption(): void {
    const question = this.args.question;
    const position = this.options.reduce((max, opt) => Math.max(max, opt.position), -1) + 1;
    const newOption = this.store.createRecord('questionnaire-field-option', {
      questionnaireField: question,
      value: '',
      position,
    });
    this.args.onChange([newOption]);

    const newOptionTextField = document.getElementById(`${this.args.inputId}-option-${position}`);
    newOptionTextField?.focus();
  }

  @action
  deleteOption(option: QuestionnaireFieldOption): void {
    option.deleteRecord();
    // update positions for remaining items to fill in any gaps
    this.options.forEach((opt, index) => (opt.position = index + 1));
    this.args.onChange([option, ...this.options]);
  }

  @action
  modifyOption(option: QuestionnaireFieldOption, newValue = ''): void {
    option.value = newValue;
    this.args.onChange([option]);
  }

  @action
  reorderOptions(options: QuestionnaireFieldOption[]): void {
    options.forEach((option, index) => {
      option.position = index + 1;
    });

    this.args.onChange(options);
  }

  @action
  setCorrectAnswer(option: QuestionnaireFieldOption): void {
    if (this.isMultipleSelection) {
      // for multiple-selection questions, toggle the "correct" state of this option
      option.isCorrect = !option.isCorrect;
      this.args.onChange([option]);
    } else {
      // for single-selection questions, mark this option as "correct" and all other options as not
      this.options.forEach((opt) => (opt.isCorrect = false));
      option.isCorrect = true;
      this.args.onChange(this.options);
    }
  }

  @action
  setName(name: string): void {
    const question = this.localizedQuestion;
    question.name = name;
    this.args.onChange([question]);
  }

  @action
  toggleSingleOrMultipleSelection(multiple: boolean): void {
    const question = this.args.question;
    if (multiple) {
      question.kind = SignInFieldKind.MULTIPLE_SELECTION;
    } else {
      question.kind = SignInFieldKind.SINGLE_SELECTION;
    }
    // unselect answers marked as "correct" when changing between single/multiple selection
    this.options.forEach((option) => (option.isCorrect = false));
    this.args.onChange([question, ...this.options]);
  }

  <template>
    <SignInFieldsWrapper
      {{sortableItem model=@question groupName=@dragGroupName}}
      class="z-20 relative"
      @cardClass="!max-w-[initial]"
      @isCarded={{false}}
      @isDeletable={{and @isDeletable (not @readOnly)}}
      @isDraggable={{@isDraggable}}
      @onDelete={{fn @deleteQuestion @question}}
      data-test-questionnaire-question={{@question.position}}
      ...attributes
    >
      <div class="flex space-x-8">
        <div class="flex-auto space-y-4">
          <div>
            <TranslatableContent
              @selectedLocale={{@selectedLocale}}
              @defaultLocale={{@defaultLocale}}
              @showLanguagePill={{true}}
            >
              <:original>
                <span class="text-carbon-50 text-sm" data-test-original-language-question-name>{{@question.name}}</span>
              </:original>
              <:selected as |isTranslating|>
                <textarea
                  id={{@inputId}}
                  placeholder="Question"
                  value={{this.localizedQuestion.name}}
                  {{on "input" (pick "target.value" this.setName)}}
                  class="{{if isTranslating '!mb-2' '!m-0'}}
                    {{if @isRTL 'direction-rtl'}}
                    {{if this.hasErrors 'error'}}"
                  dir={{if @isRTL "rtl"}}
                  disabled={{@readOnly}}
                  rows="1"
                  {{autoresize this.localizedQuestion.name maxHeight="5rem"}}
                  data-test-question-name
                />
              </:selected>
            </TranslatableContent>
            {{#if this.hasErrors}}
              <label for={{@inputId}} class="error-description !text-papaya-50 text-xs" data-test-question-errors>
                {{#each (@validator.questionErrors @question) as |error|}}
                  {{error}}<br />
                {{/each}}
              </label>
            {{/if}}
          </div>

          <div class="space-y-2">
            <div class="flex items-center space-x-4">
              <div class="w-8 text-center text-xs">
                Correct answer
              </div>
              <div class="flex-auto"></div>
              <div class="flex-none">
                {{#let (concat @inputId "-multi-toggle") as |toggleInputId|}}
                  <SlideToggle
                    class="toggle--tiny"
                    @id={{toggleInputId}}
                    @isChecked={{this.isMultipleSelection}}
                    @isDisabled={{not this.canToggleSingleOrMultipleSelection}}
                    @onClick={{this.toggleSingleOrMultipleSelection}}
                    data-test-single-multiple-toggle
                  />
                  <label for={{toggleInputId}} class="text-sm">
                    Multi-select
                  </label>
                {{/let}}
              </div>
            </div>

            <ol
              class="list-none p-0 mb-0"
              {{sortableGroup
                onChange=this.reorderOptions
                groupName=this.dragGroupNameOptions
                disabled=(not this.canDragOptions)
              }}
            >
              {{#each (sortBy "position" this.options) as |option|}}
                <QuestionOption
                  {{sortableItem model=option groupName=this.dragGroupNameOptions}}
                  @defaultLocale={{@defaultLocale}}
                  @deleteOption={{this.deleteOption}}
                  @isDraggable={{this.canDragOptions}}
                  @inputId={{@inputId}}
                  @isMultipleSelection={{this.isMultipleSelection}}
                  @isRTL={{@isRTL}}
                  @modifyOption={{this.modifyOption}}
                  @option={{option}}
                  @readOnly={{@readOnly}}
                  @selectedLocale={{@selectedLocale}}
                  @setCorrectAnswer={{this.setCorrectAnswer}}
                  @showDeleteOptionButton={{this.showDeleteOptionButtons}}
                  @validator={{@validator}}
                />

              {{/each}}
            </ol>
          </div>

          {{#if this.showAddOptionButton}}
            <div>
              <button
                type="button"
                class="btn btn--outline ml-12"
                disabled={{this.hasMaxOptions}}
                {{on "click" this.addNewOption}}
                data-test-add-option
              >
                {{svgJar "plus-round"}}
                Add option
              </button>
            </div>
          {{/if}}
        </div>
      </div>
    </SignInFieldsWrapper>
  </template>
}
